//MirrorVideo.tsx — the mirror surface itself

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { UpscaleMode } from '../config/videoPreference'
import type { LatencyStats, TryOnController } from '../tryon/tryOnController'
import { createNeuralEngine } from '../upscale/neuralEngine'
import { createSidecarEngine } from '../upscale/sidecarEngine'
import { Upscaler } from './upscaler'

interface UpscaleEngine {
	readonly label: string
	render(video: HTMLVideoElement): void
	dispose(): void
	//Engine-specific extras for the stats HUD (e.g. sidecar round-trip time).
	stats?(): string
}

interface HudStats {
	readonly engine: string
	readonly source: string
	readonly output: string
	readonly fps: number
	readonly renderMs: number
	readonly extra: string
}

async function createEngine(mode: UpscaleMode, canvas: HTMLCanvasElement): Promise<UpscaleEngine> {
	if (mode === 'neural') return createNeuralEngine(canvas)
	if (mode === 'sidecar') return createSidecarEngine(canvas)
	const shader = new Upscaler(canvas)
	return {
		label: 'shader',
		render: (video) => shader.render(video),
		dispose: () => shader.dispose(),
	}
}

interface Props {
	controller: TryOnController
	mirror: boolean
	upscaleMode: UpscaleMode
	showUpscaleStats: boolean
	latency: LatencyStats | null
	placeholder: string
}

export function MirrorVideo({
	controller,
	mirror,
	upscaleMode,
	showUpscaleStats,
	latency,
	placeholder,
}: Props) {
	const videoRef = useRef<HTMLVideoElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const [hasStream, setHasStream] = useState(false)
	const [isLive, setIsLive] = useState(false)
	//The mode whose engine refused to start (no WebGPU, sidecar absent, no WebGL).
	//Fallback ladder: chosen mode -> shader -> plain video.
	const [failedMode, setFailedMode] = useState<UpscaleMode | null>(null)
	const [isVideoPortrait, setIsVideoPortrait] = useState<boolean | null>(null)
	const [videoSize, setVideoSize] = useState('')
	const [hudStats, setHudStats] = useState<HudStats | null>(null)
	const [isViewportPortrait, setIsViewportPortrait] = useState(
		() => window.innerHeight > window.innerWidth,
	)

	//OS display rotation and window resizes both land here.
	useEffect(() => {
		const onResize = () => setIsViewportPortrait(window.innerHeight > window.innerWidth)
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [])

	const readVideoShape = () => {
		const element = videoRef.current
		if (!element || !element.videoWidth || !element.videoHeight) return
		setIsVideoPortrait(element.videoHeight > element.videoWidth)
		setVideoSize(`${element.videoWidth}×${element.videoHeight}`)
	}

	useEffect(() => {
		setFailedMode(null)
	}, [upscaleMode])

	const effectiveMode: UpscaleMode =
		failedMode === null ? upscaleMode : failedMode === 'shader' ? 'off' : 'shader'
	const isUpscaling = effectiveMode !== 'off' && isLive

	//Only the generative Lucy frames get the upscale pass; the raw camera preview is
	//shown untouched. isUpscaling in the deps also fixes the mount-order race: the
	//canvas only exists once live, and this effect re-runs after it has mounted.
	useEffect(() => {
		if (!isUpscaling) return
		const canvas = canvasRef.current
		const video = videoRef.current
		if (!canvas || !video) return
		let disposed = false
		let engine: UpscaleEngine | null = null
		let frame = 0
		createEngine(effectiveMode, canvas)
			.then((created) => {
				if (disposed) {
					created.dispose()
					return
				}
				engine = created
				console.info(`[upscale] ${created.label} engine active`)
				let frameCount = 0
				let renderMsSum = 0
				let windowStart = performance.now()
				const loop = () => {
					frame = requestAnimationFrame(loop)
					try {
						const before = performance.now()
						created.render(video)
						renderMsSum += performance.now() - before
						frameCount += 1
						const elapsed = performance.now() - windowStart
						if (elapsed >= 500) {
							const canvasNow = canvasRef.current
							setHudStats({
								engine: created.label,
								source: `${video.videoWidth}\u00d7${video.videoHeight}`,
								output: canvasNow ? `${canvasNow.width}\u00d7${canvasNow.height}` : '-',
								fps: (frameCount * 1000) / elapsed,
								renderMs: renderMsSum / frameCount,
								extra: created.stats?.() ?? '',
							})
							frameCount = 0
							renderMsSum = 0
							windowStart = performance.now()
						}
					} catch (cause) {
						//A mid-session engine death (sidecar gone, GPU lost) falls back instead
						//of leaving a dead canvas on the mirror.
						cancelAnimationFrame(frame)
						if (disposed) return
						console.warn(`Upscale mode "${effectiveMode}" died mid-session, falling back`, cause)
						setFailedMode(effectiveMode)
					}
				}
				frame = requestAnimationFrame(loop)
			})
			.catch((cause) => {
				if (disposed) return
				console.warn(`Upscale mode "${effectiveMode}" unavailable, falling back`, cause)
				setFailedMode(effectiveMode)
			})
		return () => {
			disposed = true
			cancelAnimationFrame(frame)
			engine?.dispose()
			setHudStats(null)
		}
	}, [effectiveMode, isUpscaling])

	useEffect(() => {
		return controller.setStreamSink((stream, live) => {
			const element = videoRef.current
			setHasStream(stream !== null)
			setIsLive(live)
			if (!element) return
			if (element.srcObject !== stream) element.srcObject = stream
			//Autoplay is permitted because the element is muted; the user gesture that
			//started the session covers the rest.
			if (stream) void element.play().catch(() => undefined)
		})
	}, [controller])

	const surfaceStyle = videoStyle(mirror, isLive, isVideoPortrait, isViewportPortrait)
	//The neural canvas carries the frame at source scale, so CSS does the fitting;
	//shader and sidecar render at the display grid themselves and need none.
	const fittedStyle: CSSProperties | undefined =
		effectiveMode !== 'neural'
			? undefined
			: {
					objectFit:
						isVideoPortrait === null || isVideoPortrait === isViewportPortrait
							? 'cover'
							: 'contain',
				}

	return (
		<div className="mirror">
			{/* The video stays mounted as the decode source even while the canvas presents. */}
			<video
				ref={videoRef}
				className="mirror-video"
				style={isUpscaling ? { ...surfaceStyle, visibility: 'hidden' } : surfaceStyle}
				onLoadedMetadata={readVideoShape}
				onResize={readVideoShape}
				muted
				playsInline
				autoPlay
			/>
			{/* Keyed by mode: a canvas is forever bound to its first context type, so a
			    webgl2 shader canvas can never become the neural engine's webgpu canvas. */}
			{isUpscaling && (
				<canvas key={effectiveMode} ref={canvasRef} className="mirror-canvas" style={fittedStyle} />
			)}
			{showUpscaleStats && hasStream && (
				<div className="upscale-stats">
					{isUpscaling && hudStats ? (
						<>
							<span>engine {hudStats.engine}{failedMode ? ' (fallback)' : ''}</span>
							<span>in {hudStats.source} → out {hudStats.output}</span>
							<span>display {Math.round(window.innerWidth * devicePixelRatio)}×{Math.round(window.innerHeight * devicePixelRatio)} @{devicePixelRatio}x</span>
							<span>{hudStats.fps.toFixed(0)} fps · render {hudStats.renderMs.toFixed(2)} ms</span>
							{hudStats.extra && <span>{hudStats.extra}</span>}
						</>
					) : (
						<span>engine none · raw {isLive ? 'lucy' : 'camera'} feed {videoSize}</span>
					)}
					{latency && (
						<span>
							lag median {ms(latency.medianMs)} · p90 {ms(latency.p90Ms)} · first frame{' '}
							{ms(latency.firstFrameMs)} · rtt {ms(latency.rttMs)} · n={latency.samples}
						</span>
					)}
				</div>
			)}
			{!hasStream && <p className="mirror-placeholder">{placeholder}</p>}
		</div>
	)
}

const ms = (value: number | null): string => (value === null ? '–' : `${Math.round(value)} ms`)

function videoStyle(
	mirror: boolean,
	isLive: boolean,
	isVideoPortrait: boolean | null,
	isViewportPortrait: boolean,
): CSSProperties {
	//When live, the SDK has already pre-flipped the published frames, so flipping again here
	//would cancel it out.
	const flip = mirror && !isLive ? ' scaleX(-1)' : ''
	//Fill when the frame shape matches the screen, fit when it does not: a landscape feed
	//on a vertical screen must never be fill-cropped down to a thin slice. Unknown shape
	//keeps the full-bleed default.
	const matches = isVideoPortrait === null || isVideoPortrait === isViewportPortrait
	return {
		width: '100dvw',
		height: '100dvh',
		objectFit: matches ? 'cover' : 'contain',
		transform: `translate(-50%, -50%)${flip}`,
	}
}
